import { CalendarClock, RotateCw } from "lucide-react";

/**
 * ReceivableProbabilityMock — focuses ONLY on the probability badge of
 * a receivable row. iter-211.2 — replaced the full /receivables
 * screenshot per user feedback ("solo la parte de la probabilidad").
 *
 * Renders a tight stack of 3 receivable rows (HIGH / MEDIUM / LOW)
 * with developer clients so the value prop is immediately visible.
 */
const ROWS = [
  {
    initial: "V",
    color: "from-emerald-500 to-emerald-700",
    client: "Vercel SaaS Consulting",
    due: "Vence en 4 días",
    amount: "$ 18.670.500",
    sub: "≈ 4.500 USD",
    chip: { label: "HIGH", tone: "emerald" },
    sub_chip: { label: "Suscripción · Mensual", tone: "purple" },
  },
  {
    initial: "G",
    color: "from-sky-500 to-sky-700",
    client: "GitLab Enterprise · Setup",
    due: "Vence el 1 de jul",
    amount: "$ 14.200.000",
    chip: { label: "MEDIUM", tone: "amber" },
  },
  {
    initial: "S",
    color: "from-purple-500 to-purple-700",
    client: "Startup Y · Discovery",
    due: "Vence el 15 de jul",
    amount: "$ 6.800.000",
    chip: { label: "LOW", tone: "rose" },
  },
];

const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-800 ring-emerald-200",
  amber: "bg-amber-100 text-amber-800 ring-amber-200",
  rose: "bg-rose-100 text-rose-800 ring-rose-200",
  purple: "bg-purple-50 text-purple-700 ring-purple-200",
};

export function ReceivableProbabilityMock() {
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
      data-testid="mock-receivable-probability"
    >
      <div className="px-4 pt-4 pb-2 flex items-center justify-between border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">
            Cobros pendientes
          </div>
          <div className="font-display text-sm font-semibold text-slate-900 mt-0.5">
            Probabilidad real de cobro
          </div>
        </div>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">
          Total esperado · 39.6M COP
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {ROWS.map((r, i) => (
          <div
            key={i}
            className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50/60 transition-colors"
            data-testid={`prob-row-${i}`}
          >
            <div
              className={`h-9 w-9 rounded-full bg-gradient-to-br ${r.color} text-white font-bold flex items-center justify-center text-sm shadow-sm flex-shrink-0`}
              aria-hidden="true"
            >
              {r.initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 text-sm truncate flex items-center gap-2">
                {r.client}
                {r.sub_chip && (
                  <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider ring-1 rounded-full px-1.5 py-0.5 ${TONE_CLASSES[r.sub_chip.tone]}`}>
                    <RotateCw className="h-2 w-2" /> {r.sub_chip.label}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5">
                <CalendarClock className="h-3 w-3" />
                {r.due}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span
                className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider ring-1 rounded-full px-2 py-0.5 ${TONE_CLASSES[r.chip.tone]}`}
                data-testid={`prob-badge-${i}`}
              >
                {r.chip.label}
              </span>
              <div className="text-sm font-bold tabular-nums text-slate-900 leading-tight">
                {r.amount}
              </div>
              {r.sub && (
                <div className="text-[10px] text-slate-400 tabular-nums leading-tight">
                  {r.sub}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
